import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i0 from '@angular/core';
import { Directive, Input, Injectable, EventEmitter, Optional, Output, HostBinding, Component, ViewChild, ContentChild, HostListener, ViewEncapsulation, Host, NgModule } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { ENTER, SPACE } from '@anthem/uxd/util';

const EXPAND_ANIMATION_TIMING = "500ms cubic-bezier(0.4,0.0,0.2,1)";
const COLLAPSE_ANIMATION_TIMING = "250ms cubic-bezier(0.4,0.0,0.2,1)";
const accordionAnimations = {
    accordionBodyExpansion: trigger("expand", [
        state("collapsed", style({
            height: "0",
            opacity: "0",
            visibility: "hidden",
            overflow: "hidden"
        })),
        state("expanded", style({
            height: "*",
            opacity: "1",
            visibility: "visible"
        })),
        state("*", style({
            height: "0",
            opacity: "0",
            visibility: "hidden"
        })),
        transition("expanded => collapsed", animate(EXPAND_ANIMATION_TIMING)),
        transition("collapsed => expanded", animate(COLLAPSE_ANIMATION_TIMING)),
        transition("* => expanded", animate(EXPAND_ANIMATION_TIMING)),
        transition("* => collapsed", animate(COLLAPSE_ANIMATION_TIMING)),
        transition("* => *", animate(COLLAPSE_ANIMATION_TIMING)),
    ])
};

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/** Used to generated unique HTML IDs. */
let accordionID = 0;
/**
 * A version of Google's `cdkAccordion` directive modified for
 * the benefit of the UXD site. Where possible, Inputs have been
 * simplified and variable names made more readable.
 *
 *
 * @see cdkAccordion for original implementation of Material accordion.
 * {@link https://github.com/angular/material2/blob/master/src/cdk/accordion/accordion.ts}
 */
class AccordionDirective {
    constructor() {
        /** Observable stream that emits true/false whenever openAll/closeAll methods
         * has been called. */
        this._openCloseAllStream = new Subject();
        /** Unique HTML Id representing an accordion.  */
        this._uniqueID = `uxd-accordion-${accordionID++}`;
    }
    /** Returns the readonly HTML Id defined earlier. */
    get id() {
        return this._uniqueID;
    }
    ngOnInit() {
        /** If no type is specified, assume allows multiple items to be open (isAccordion = false). */
        this.isAccordion = this.isAccordion ? this.isAccordion : false;
    }
    /** Opens all of the accordion items if isAccordion is false. */
    openAll() {
        this._openCloseAll(true);
    }
    /** Closes all of the accordion items if isAccordion is false. */
    closeAll() {
        this._openCloseAll(false);
    }
    /** Emits a boolean value to all subscribed accordion items. */
    _openCloseAll(isExpanded) {
        /*  Expand/collapse isAccordion must be false.
         *  isAccordion being true cannot have all of their items opened/closed,
         *  so it does not make  sense to provide openAll/closeAll functionality to them.  */
        if (!this.isAccordion) {
            this._openCloseAllStream.next(isExpanded);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AccordionDirective, selector: "uxdAccordion, [uxdAccordion]", inputs: { isAccordion: "isAccordion" }, exportAs: ["uxdAccordion"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'uxdAccordion, [uxdAccordion]',
                    exportAs: 'uxdAccordion'
                }]
        }], propDecorators: { isAccordion: [{
                type: Input
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
class AccordionSelectionDispatcherService {
    constructor() {
        this._listeners = [];
    }
    notify(id, name) {
        for (const listener of this._listeners) {
            listener(id, name);
        }
    }
    listen(listener) {
        this._listeners.push(listener);
    }
    ngOnDestroy() {
        this._listeners = [];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordionSelectionDispatcherService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordionSelectionDispatcherService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordionSelectionDispatcherService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let accordionItemID = 0;
/**
 * A version of Google's `cdkAccordionItem` directive modified for
 * the benefit of the UXD site. Where possible, Inputs have been
 * simplified and variable names made more readable.
 *
 * @see cdkAccordionItem for original implementation of Material accordion.
 * {@link https://github.com/angular/material2/blob/master/src/cdk/accordion/accordion-item.ts}
 *
 */
class AccordionItemDirective {
    /** Whether the current accordion item should be expanded. */
    get expanded() {
        return this._isExpanded;
    }
    set expanded(isExpanded) {
        this._setIsExpanded(isExpanded);
    }
    /** Toggles a `is-expanded` class on whatever element the
     * AccordionItem directive is attached to.
     *
     * This allows styling of elements, like icons, to differ
     * on expanded/collapsed state.
     */
    get isExpandedClass() {
        return this.expanded;
    }
    /** Returns the readonly HTML Id defined earlier. */
    get id() {
        return this._uniqueID;
    }
    /** Toggle the accordion item's state;
     * if it is expanded, make it collapsed;
     * if it is collapsed, make it expanded. */
    toggle() {
        this.expanded = !this._isExpanded;
    }
    /** Open the accordion item, making it expanded. */
    open() {
        this.expanded = true;
    }
    /** Close the accordion item, making it collapsed. */
    close() {
        this.expanded = false;
    }
    /**
     * A subscription to the parent accordion's openAll/closeAll stream.
     *
     * Whenever openAll/closeAll is called from inside the accordion, this subscription
     * will alert this accordion item to expand or collapse.
     *
     * @memberof AccordionItemDirective
     */
    _subscribeToOpenCloseStream() {
        return this.accordion._openCloseAllStream.subscribe((isExpaned) => {
            this.expanded = isExpaned;
        });
    }
    constructor(accordion, _changeDetectorRef, _selectionDispatcher) {
        this.accordion = accordion;
        this._changeDetectorRef = _changeDetectorRef;
        this._selectionDispatcher = _selectionDispatcher;
        /** A reference to the observable stream declared in the parent accordion,
         * which emits true/false whenever openAll/closeAll methods have been called.*/
        this._openCloseAllStream = Subscription.EMPTY;
        /** Unique HTML Id representing an accordion item.  */
        this._uniqueID = `uxd-accordion-item-${accordionItemID++}`;
        /** Emits a `void` event every time the item is opened. */
        this.opened = new EventEmitter();
        /** Emits a `void` event every time the item is closed. */
        this.closed = new EventEmitter();
        /** Emits the current expanded-state as a boolean whenever the state of the
         *  accordion item changes*/
        this.expandedChanged = new EventEmitter();
        this._isExpanded = false;
        /* Every time a sibling accordion item becomes expanded, an event is fired with the id
         * of both the accordion and the selected accordion item.
         *
         * Each accordion item listens for state changes to its siblings, and if input isAccordion
         * is true reacts accordingly by closing.  */
        _selectionDispatcher.listen((id, accordionID) => {
            if (!this.accordion) {
                return;
            }
            /* Check if the parent input isAccordion is true`. */
            if (this.accordion.isAccordion) {
                /* Ensure that the parent accordion is the same accordion that contained the change,
                 * and that this item was not the source of the change. */
                if (this.accordion.id === accordionID && id !== this.id) {
                    this.expanded = false; // Closes this accordion item
                }
            }
        });
        /* If the parent accordion exists, subscribe to its openCloseAll stream. */
        if (accordion && accordion._openCloseAllStream) {
            this._openCloseAllStream = this._subscribeToOpenCloseStream();
        }
    }
    /**
     * On destruction of the directive, also destroy/close all references
     * to observable streams: `opened`, `closed`, `expandedChanged`, etc.
     * This helps prevent memory leaks.
     *
     * @memberof AccordionItemDirective
     */
    ngOnDestroy() {
        this.opened.complete();
        this.closed.complete();
        this.expandedChanged.complete();
        this._openCloseAllStream.unsubscribe();
    }
    _setIsExpanded(isExpanded) {
        /* Update `_isExpanded` only if it has changed,
         * Otherwise do nothing. */
        if (this._isExpanded !== isExpanded) {
            this._isExpanded = isExpanded;
            /* Emit the new state of the accordion item. */
            this.expandedChanged.emit(isExpanded);
            if (isExpanded && this.accordion) {
                /* If the accordion item is now open, emit an event along the `opened` stream,
                 * and notify all sibling items that this is now opened */
                this.opened.emit();
                this._selectionDispatcher.notify(this.id, this.accordion.id);
            }
            else {
                /* Otherwise, just emit an event along the `closed` stream. */
                this.closed.emit();
            }
            /* Finally —
             * Inform Angular that values have changed, and that the
             * the template needs to be re-rendered to reflect those changes. */
            this._changeDetectorRef.markForCheck();
        }
        return this._isExpanded;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordionItemDirective, deps: [{ token: AccordionDirective, optional: true }, { token: i0.ChangeDetectorRef }, { token: AccordionSelectionDispatcherService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AccordionItemDirective, selector: "[uxdAccordionItem]", inputs: { expanded: "expanded" }, outputs: { opened: "opened", closed: "closed", expandedChanged: "expandedChanged" }, host: { properties: { "class.is-expanded": "this.isExpandedClass" } }, exportAs: ["uxdAccordionItem"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordionItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[uxdAccordionItem]',
                    exportAs: 'uxdAccordionItem'
                }]
        }], ctorParameters: function () { return [{ type: AccordionDirective, decorators: [{
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }, { type: AccordionSelectionDispatcherService }]; }, propDecorators: { opened: [{
                type: Output
            }], closed: [{
                type: Output
            }], expandedChanged: [{
                type: Output
            }], expanded: [{
                type: Input
            }], isExpandedClass: [{
                type: HostBinding,
                args: ['class.is-expanded']
            }] } });

var ExpandCollapseTheme;
(function (ExpandCollapseTheme) {
    ExpandCollapseTheme["PRIMARY"] = "ant-theme-primary";
    ExpandCollapseTheme["SECONDARY"] = "ant-theme-secondary";
})(ExpandCollapseTheme || (ExpandCollapseTheme = {}));

//istanbul ignore next
class CollapseItemCmp {
    constructor() {
        this._isExpanded = false;
        this.startBlockExpanded = false;
    }
    /** Whether the current accordion item should be expanded. */
    get expanded() {
        return this._isExpanded;
    }
    set expanded(isExpanded) {
        this._isExpanded = isExpanded;
    }
    get themeClass() {
        return this.style || ExpandCollapseTheme.PRIMARY;
    }
    get isExpanded() {
        return this.accordionItem && this.accordionItem.expanded;
    }
    changeDisplay() {
        return !this.isExpanded;
    }
    get display() {
        return this.changeDisplay;
    }
    ngOnInit() {
        if (!this.collapseItem.data) {
            this.headingContent = this.collapseItem.headingContent;
            this.bodyContent = this.collapseItem.bodyContent;
            // this.startBlockExpanded = this.collapseItem.startBlockExpanded;
        }
        this.color = this.collapseItem.color;
        this.mobileOnly = this.collapseItem.mobileOnly;
        this.limitOne = this.collapseItem.grouped;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollapseItemCmp, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CollapseItemCmp, selector: "[data-uxd-collapse-item-cmp]", inputs: { collapseItem: "collapseItem", style: "style", expanded: "expanded", headingContent: "headingContent", bodyContent: "bodyContent", id: "id", startBlockExpanded: "startBlockExpanded" }, host: { classAttribute: "uxd-collapse-item-cmp" }, viewQueries: [{ propertyName: "accordionItem", first: true, predicate: ["accordionItem"], descendants: true }, { propertyName: "host", first: true, predicate: ["host"], descendants: true }], ngImport: i0, template: `
  <div uxdAccordionItem #accordionItem="uxdAccordionItem" #host [expanded]="expanded" [ngClass]="themeClass"
    class="collapse-block alert-pattern-v2 js-animateable" [ngClass]="{'ant-block-expanded' : accordionItem.expanded}">
    <div class="ant-collapse" [ngClass]="{'blue': (color === 'blue'), 'ant-mobile-collapse': (mobileOnly === true)}">
      <h3 class="ant-collapse-heading">
        <button type="button" class="ant-collapse-button" [id]="id" [attr.aria-controls]="id+'_section'"  (click)="accordionItem.toggle()">
          <span class="accordion-button-icon" [ngClass]="{'hideIcon': (mobileOnly === true)}" aria-hidden="true"></span>
          <span class="collapsible-heading-text" [innerHTML]="headingContent"></span>
        </button>
      </h3>
      <div class="ant-collapse-body content-control" [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody>
        <div class="body-content" [id]="id+'_section'">
          <ng-content> </ng-content>
          <div *ngIf="bodyContent" [innerHTML]="bodyContent">
          </div>
        </div>
      </div>
    </div>
  </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: AccordionItemDirective, selector: "[uxdAccordionItem]", inputs: ["expanded"], outputs: ["opened", "closed", "expandedChanged"], exportAs: ["uxdAccordionItem"] }], animations: [accordionAnimations.accordionBodyExpansion] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollapseItemCmp, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-collapse-item-cmp]',
                    template: `
  <div uxdAccordionItem #accordionItem="uxdAccordionItem" #host [expanded]="expanded" [ngClass]="themeClass"
    class="collapse-block alert-pattern-v2 js-animateable" [ngClass]="{'ant-block-expanded' : accordionItem.expanded}">
    <div class="ant-collapse" [ngClass]="{'blue': (color === 'blue'), 'ant-mobile-collapse': (mobileOnly === true)}">
      <h3 class="ant-collapse-heading">
        <button type="button" class="ant-collapse-button" [id]="id" [attr.aria-controls]="id+'_section'"  (click)="accordionItem.toggle()">
          <span class="accordion-button-icon" [ngClass]="{'hideIcon': (mobileOnly === true)}" aria-hidden="true"></span>
          <span class="collapsible-heading-text" [innerHTML]="headingContent"></span>
        </button>
      </h3>
      <div class="ant-collapse-body content-control" [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody>
        <div class="body-content" [id]="id+'_section'">
          <ng-content> </ng-content>
          <div *ngIf="bodyContent" [innerHTML]="bodyContent">
          </div>
        </div>
      </div>
    </div>
  </div>`,
                    host: {
                        class: 'uxd-collapse-item-cmp'
                    },
                    animations: [accordionAnimations.accordionBodyExpansion]
                }]
        }], propDecorators: { collapseItem: [{
                type: Input
            }], style: [{
                type: Input
            }], expanded: [{
                type: Input
            }], headingContent: [{
                type: Input
            }], bodyContent: [{
                type: Input
            }], id: [{
                type: Input
            }], startBlockExpanded: [{
                type: Input,
                args: ['startBlockExpanded']
            }], accordionItem: [{
                type: ViewChild,
                args: ['accordionItem']
            }], host: [{
                type: ViewChild,
                args: ['host']
            }] } });

class AlternateToggleBodyComponent {
    constructor(_toggleBodyRef) {
        this._toggleBodyRef = _toggleBodyRef;
        this._isExpanded = false;
    }
    buildToggleBodyId() {
        let id = 'alt-toggle-body-container-' + this._toggleNumber;
        return id;
    }
    set isExpanded(bool) {
        this._isExpanded = bool;
    }
    get isExpanded() {
        return this._isExpanded;
    }
    set toggleNumber(toggleNum) {
        this._toggleNumber = toggleNum;
    }
    get toggleNumber() {
        return this._toggleNumber;
    }
    get toggleBodyRef() {
        return this._toggleBodyRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlternateToggleBodyComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AlternateToggleBodyComponent, selector: "[data-uxd-alt-toggle-body-cmp]", host: { attributes: { "tabindex": "-1" }, properties: { "class.alt-toggle-body": "true", "class.closed": "!isExpanded", "id": "buildToggleBodyId()" } }, ngImport: i0, template: `
      <div [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody>
        <ng-content></ng-content>
      </div>
  `, isInline: true, animations: [accordionAnimations.accordionBodyExpansion] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlternateToggleBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-alt-toggle-body-cmp]',
                    host: {
                        '[class.alt-toggle-body]': 'true',
                        '[class.closed]': '!isExpanded',
                        '[id]': 'buildToggleBodyId()',
                        tabindex: '-1'
                    },
                    template: `
      <div [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody>
        <ng-content></ng-content>
      </div>
  `,
                    animations: [accordionAnimations.accordionBodyExpansion]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

// DEPRECATED
/**
 *  Toggle component with full transclusion capabilities
 */
class AlternateToggleComponent {
    constructor(_renderer) {
        this._renderer = _renderer;
        if (this.altToggleItem === undefined) {
            this.altToggleItem = {};
            this.altToggleItem.buttonName = '';
            this.altToggleItem.ariaButtonText = '';
        }
    }
    /**
     *  After initialization, if input value is not passed it, automatically
     *  assigns its value to 1. Primarily used for accessibility. Additional
     *  logic added to support deprecated input param
     */
    ngAfterViewInit() {
        // Subscribe value of toggle body expansion to expanded value
        // in this component.
        this.accordionItem.expandedChanged.subscribe((e) => {
            this.toggleBody.isExpanded = this.isExpanded;
        });
        // this.expanded = this._firstToggleBody.isExpanded;
        if (this.toggleNumber === undefined || this.toggleNumber === null) {
            this.toggleNumber = 1;
        }
    }
    /**
     *  After content has been confirmed in the view, set the expanded or collapse
     *  status. If the length of AlternateToggleBodyComponent is not 0, assign
     *  variable
     */
    ngAfterContentChecked() {
        // if (this.toggleBodyList.toArray().length > 0) {
        //   this._firstToggleBody = this.toggleBodyList.toArray()[0];
        // }
        // this._firstToggleBody.toggleNumber = this.toggleNumber;
    }
    /**
     *  On user click toggle the boolean state of the body between
     *  show and hide
     */
    expandClick() {
        this.accordionItem.toggle();
        // this.toggleBody.isExpanded = this.isExpanded;
        // this._firstToggleBody.isExpanded = !this._firstToggleBody.isExpanded;
        // this.expanded = this._firstToggleBody.isExpanded;
        // this.calculateMaxHeight(this.expanded);
        this.attachAnalytics();
    }
    onKeypress(event) {
        /* If the key pressed was either the spacebar (32) or the enter key (13),
         * expand. */
        if (event.keyCode === 32 || event.keyCode === 13) {
            if (event.keyCode === 32) {
                event.preventDefault();
            }
            this.accordionItem.toggle();
            this.attachAnalytics();
        }
    }
    get isExpanded() {
        return this.accordionItem && this.accordionItem.expanded;
    }
    /**
     * On window resize/change, close the expanders to re-adjust height style value
     */
    onResize(event) {
        // this.calculateMaxHeight(this.expanded);
    }
    /**
     *  Searches for a analyticsText string in the json - if provided, it will return that string else null
     */
    attachAnalytics() {
        if (this.altToggleItem.analyticsText) {
            if (this.isExpanded) {
                return 'expand' + this.altToggleItem.analyticsText;
            }
            else {
                return 'collapse' + this.altToggleItem.analyticsText;
            }
        }
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlternateToggleComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AlternateToggleComponent, selector: "[data-uxd-alt-toggle-cmp]", inputs: { altToggleItem: "altToggleItem", toggleNumber: "toggleNumber" }, host: { listeners: { "window:resize": "onResize($event)" }, properties: { "class.clearfix": "true", "class.alt-toggle-cmp": "true" } }, queries: [{ propertyName: "toggleBody", first: true, predicate: AlternateToggleBodyComponent, descendants: true }], viewQueries: [{ propertyName: "accordionItem", first: true, predicate: ["accordionItem"], descendants: true }], ngImport: i0, template: `
    <!-- Transcluded content goes in here, including header + body -->
    <div class="alt-toggle" uxdAccordionItem #accordionItem="uxdAccordionItem">
      <div class="alt-toggle-header" [id]="'alt-toggle-header-container-'+toggleNumber">
        <ng-content></ng-content>
      </div>
      <!-- End transclusion -->

      <div class="alt-toggle-icon" role="button" tabindex="0" [attr.aria-expanded]="isExpanded" (click) ="expandClick()" (keydown)="onKeypress($event)" [attr.aria-controls]="'alt-toggle-body-container-'+ toggleNumber"
      [attr.name]="" [attr.data-analytics]="attachAnalytics()">
        <span class="fa md" [ngClass] ="isExpanded ? 'alt-toggle-minus' : 'alt-toggle-plus'" [attr.data-analytics]="attachAnalytics()"></span>
        <span class="sr-only" [innerText]="altToggleItem.ariaButtonText + ' ' + altToggleItem.buttonName + ' ' + toggleNumber" [attr.data-analytics]="attachAnalytics()"></span>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: AccordionItemDirective, selector: "[uxdAccordionItem]", inputs: ["expanded"], outputs: ["opened", "closed", "expandedChanged"], exportAs: ["uxdAccordionItem"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlternateToggleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-alt-toggle-cmp]',
                    host: {
                        '[class.clearfix]': 'true',
                        '[class.alt-toggle-cmp]': 'true'
                    },
                    template: `
    <!-- Transcluded content goes in here, including header + body -->
    <div class="alt-toggle" uxdAccordionItem #accordionItem="uxdAccordionItem">
      <div class="alt-toggle-header" [id]="'alt-toggle-header-container-'+toggleNumber">
        <ng-content></ng-content>
      </div>
      <!-- End transclusion -->

      <div class="alt-toggle-icon" role="button" tabindex="0" [attr.aria-expanded]="isExpanded" (click) ="expandClick()" (keydown)="onKeypress($event)" [attr.aria-controls]="'alt-toggle-body-container-'+ toggleNumber"
      [attr.name]="" [attr.data-analytics]="attachAnalytics()">
        <span class="fa md" [ngClass] ="isExpanded ? 'alt-toggle-minus' : 'alt-toggle-plus'" [attr.data-analytics]="attachAnalytics()"></span>
        <span class="sr-only" [innerText]="altToggleItem.ariaButtonText + ' ' + altToggleItem.buttonName + ' ' + toggleNumber" [attr.data-analytics]="attachAnalytics()"></span>
      </div>
    </div>
  `
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { altToggleItem: [{
                type: Input
            }], toggleNumber: [{
                type: Input
            }], toggleBody: [{
                type: ContentChild,
                args: [AlternateToggleBodyComponent]
            }], accordionItem: [{
                type: ViewChild,
                args: ['accordionItem']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class ExpansionPanelComponent extends AccordionItemDirective {
    /**
     *  Whether the current accordion item should be expanded.
     *  Note that Input metadata is not inherited from AccordionItemDirective,
     *  so it must be re-declared here to work.
     */
    set expanded(isExpanded) {
        super._setIsExpanded(isExpanded);
    }
    get expanded() {
        return this._isExpanded;
    }
    constructor(_changeDetectorRef, accordion, _selectionDispatcher) {
        super(accordion, _changeDetectorRef, _selectionDispatcher);
        this._selectionDispatcher = _selectionDispatcher;
        this.onOpen = new EventEmitter();
        this.onClose = new EventEmitter();
        this.onDestroy = new EventEmitter();
        this.accordion = accordion;
    }
    _getExpandedState() {
        return this.expanded ? 'expanded' : 'collapsed';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpansionPanelComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: AccordionDirective, optional: true }, { token: AccordionSelectionDispatcherService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ExpansionPanelComponent, selector: "uxd-expansion-panel", inputs: { expanded: "expanded" }, outputs: { onOpen: "onOpen", onClose: "onClose", onDestroy: "onDestroy" }, host: { properties: { "attr.aria-expanded": "expanded" }, classAttribute: "uxd-expansion-panel" }, exportAs: ["uxdExpansionPanel"], usesInheritance: true, ngImport: i0, template: "<ng-content select=\"uxd-expansion-header\"></ng-content>\n<div class=\"uxd-panel-content\" [@expand]=\"_getExpandedState()\">\n  <div class=\"uxd-panel-content-body\" >\n    <ng-content></ng-content>\n  </div>\n</div>\n", animations: [accordionAnimations.accordionBodyExpansion], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpansionPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'uxd-expansion-panel', exportAs: 'uxdExpansionPanel', host: {
                        class: 'uxd-expansion-panel',
                        '[attr.aria-expanded]': 'expanded',
                    }, animations: [accordionAnimations.accordionBodyExpansion], encapsulation: ViewEncapsulation.None, template: "<ng-content select=\"uxd-expansion-header\"></ng-content>\n<div class=\"uxd-panel-content\" [@expand]=\"_getExpandedState()\">\n  <div class=\"uxd-panel-content-body\" >\n    <ng-content></ng-content>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: AccordionDirective, decorators: [{
                    type: Optional
                }] }, { type: AccordionSelectionDispatcherService }]; }, propDecorators: { expanded: [{
                type: Input
            }], onOpen: [{
                type: Output
            }], onClose: [{
                type: Output
            }], onDestroy: [{
                type: Output
            }] } });

let nextUniqueId = 0;
class ExpansionHeaderComponent {
    get isTabbable() {
        return this.tabindex > -1;
    }
    isExpanded() {
        return this.panel.expanded;
    }
    toggle() {
        this.panel.toggle();
    }
    open() {
        this.panel.open();
    }
    close() {
        this.panel.close();
    }
    _onKeydown(event) {
        const keyCode = event.keyCode;
        switch (keyCode) {
            case SPACE:
            case ENTER: {
                event.preventDefault();
                this.toggle();
                return;
            }
        }
    }
    constructor(panel) {
        this.panel = panel;
        this.tabindex = -1;
        this.ariaLabel = null;
        this.ariaLabelledby = null;
        this._uniqueId = `ant-expansion-header-${++nextUniqueId}`;
        this.id = this._uniqueId;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpansionHeaderComponent, deps: [{ token: ExpansionPanelComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ExpansionHeaderComponent, selector: "uxd-expansion-header", inputs: { tabindex: "tabindex", ariaLabel: ["aria-label", "ariaLabel"], ariaLabelledby: ["aria-labelledby", "ariaLabelledby"], id: "id" }, host: { listeners: { "keydown": "isTabbable ? _onKeydown($event) : null" }, properties: { "attr.tabindex": "-1", "id": "id", "attr.aria-label": "ariaLabel", "attr.aria-labelledby": "ariaLabelledby" } }, exportAs: ["uxdExpansionHeaderComponent"], ngImport: i0, template: "<div class=\"uxd-expansion-header\">\n  <ng-content></ng-content>\n</div>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpansionHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'uxd-expansion-header', exportAs: 'uxdExpansionHeaderComponent', host: {
                        '[attr.tabindex]': '-1',
                        '(keydown)': 'isTabbable ? _onKeydown($event) : null',
                        '[id]': 'id',
                        '[attr.aria-label]': 'ariaLabel',
                        '[attr.aria-labelledby]': 'ariaLabelledby'
                    }, template: "<div class=\"uxd-expansion-header\">\n  <ng-content></ng-content>\n</div>" }]
        }], ctorParameters: function () { return [{ type: ExpansionPanelComponent, decorators: [{
                    type: Host
                }] }]; }, propDecorators: { tabindex: [{
                type: Input,
                args: ['tabindex']
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }], ariaLabelledby: [{
                type: Input,
                args: ['aria-labelledby']
            }], id: [{
                type: Input
            }] } });

class ExpansionToggleComponent {
    get isTabbable() {
        return this.tabindex > -1;
    }
    get isExpanded() {
        return this.header && this.header.isExpanded();
    }
    constructor(header) {
        this.header = header;
        this.tabindex = 0;
        this.analytics = null;
        this.ariaLabel = null;
        this.ariaLabelledby = null;
        this.onClick = new EventEmitter();
    }
    _onClick(event) {
        if (this.header) {
            this.header.toggle();
        }
        event.stopPropagation();
        this.onClick.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpansionToggleComponent, deps: [{ token: ExpansionHeaderComponent, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ExpansionToggleComponent, selector: "uxd-expansion-toggle", inputs: { tabindex: "tabindex", analytics: "analytics", ariaLabel: ["aria-label", "ariaLabel"], ariaLabelledby: ["aria-labelledby", "ariaLabelledby"] }, outputs: { onClick: "onClick" }, host: { classAttribute: "uxd-expansion-toggle" }, ngImport: i0, template: `
  <button type="button" class="uxd-expansion-button"
    [attr.tabindex]="0"
    (click)="_onClick($event)"
    [attr.aria-expanded]="isExpanded"
    [attr.aria-labelledby]="ariaLabelledby"
    [attr.aria-label]="ariaLabel"
    [attr.data-analytics]="analytics"
    [attr.aria-hidden]="!isTabbable || null">
    <ng-content></ng-content>
  </button>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpansionToggleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "uxd-expansion-toggle",
                    host: {
                        class: "uxd-expansion-toggle"
                    },
                    template: `
  <button type="button" class="uxd-expansion-button"
    [attr.tabindex]="0"
    (click)="_onClick($event)"
    [attr.aria-expanded]="isExpanded"
    [attr.aria-labelledby]="ariaLabelledby"
    [attr.aria-label]="ariaLabel"
    [attr.data-analytics]="analytics"
    [attr.aria-hidden]="!isTabbable || null">
    <ng-content></ng-content>
  </button>`
                }]
        }], ctorParameters: function () { return [{ type: ExpansionHeaderComponent, decorators: [{
                    type: Host
                }] }]; }, propDecorators: { tabindex: [{
                type: Input,
                args: ['tabindex']
            }], analytics: [{
                type: Input
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }], ariaLabelledby: [{
                type: Input,
                args: ['aria-labelledby']
            }], onClick: [{
                type: Output
            }] } });

//istanbul ignore next
class CollapseGroupCmp {
    constructor(_element) {
        this._element = _element;
        this._jElement = this._element.nativeElement;
    }
    ngOnInit() {
        if (this.collapseGroup.limitOneBlock) {
            this._jElement.classList.add("limit-one-collapse");
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollapseGroupCmp, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CollapseGroupCmp, selector: "[data-uxd-collapse-group-cmp]", inputs: { collapseGroup: "collapseGroup" }, ngImport: i0, template: `<div data-uxd-collapse-item-cmp [ngClass]="{'addStripe': (collapseGroup.data.length > 3)}" [collapseItem]="collapseGroup" [headingContent]="collapseItem.headingContent"
    [bodyContent]="collapseItem.content" [id]="collapseItem.id + $index" *ngFor="let collapseItem of collapseGroup.data; let $index = index">
</div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: CollapseItemCmp, selector: "[data-uxd-collapse-item-cmp]", inputs: ["collapseItem", "style", "expanded", "headingContent", "bodyContent", "id", "startBlockExpanded"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CollapseGroupCmp, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-collapse-group-cmp]",
                    template: `<div data-uxd-collapse-item-cmp [ngClass]="{'addStripe': (collapseGroup.data.length > 3)}" [collapseItem]="collapseGroup" [headingContent]="collapseItem.headingContent"
    [bodyContent]="collapseItem.content" [id]="collapseItem.id + $index" *ngFor="let collapseItem of collapseGroup.data; let $index = index">
</div>`
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { collapseGroup: [{
                type: Input
            }] } });

let nextUniqueID = 0;
//istanbul ignore next
class ExpandCollapaseWcs {
    get id() {
        return this._id;
    }
    applyTheme(theme) {
        switch (theme) {
            case ExpandCollapseTheme.SECONDARY: {
                this._renderer.addClass(this.host.nativeElement, ExpandCollapseTheme.SECONDARY);
                break;
            }
            case ExpandCollapseTheme.PRIMARY: {
                this._renderer.addClass(this.host.nativeElement, ExpandCollapseTheme.PRIMARY);
                break;
            }
            default: {
                this._renderer.addClass(this.host.nativeElement, ExpandCollapseTheme.PRIMARY);
                return;
            }
        }
    }
    toggleAccordionItem() {
        this.accordionItem.toggle();
    }
    get isExpanded() {
        return this.accordionItem && this.accordionItem.expanded;
    }
    constructor(_renderer) {
        this._renderer = _renderer;
        this._id = `${nextUniqueID++}`;
    }
    ngAfterViewInit() {
        if (!this.style) {
            this.style = ExpandCollapseTheme.PRIMARY;
        }
        this.applyTheme(this.style);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpandCollapaseWcs, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ExpandCollapaseWcs, selector: "[data-uxd-expand-collapse-wcs]", inputs: { style: "style" }, host: { classAttribute: "uxd-expand-collapse-wcs" }, viewQueries: [{ propertyName: "accordionItem", first: true, predicate: ["accordionItem"], descendants: true, static: true }, { propertyName: "host", first: true, predicate: ["host"], descendants: true, static: true }], ngImport: i0, template: `<div uxdAccordionItem #accordionItem="uxdAccordionItem" #host class="collapse-block js-animateable" [ngClass]="{'ant-block-expanded' : accordionItem.expanded}">
              <div class="ant-collapse blue">
                <div class="ant-collapse-heading">
                  <button type="button"
                    [attr.aria-controls]="'aria-helper-id-'+id"
                    [attr.aria-expanded]="isExpanded"
                    (click)="toggleAccordionItem()" class="ant-collapse-button">
                    <span class="accordion-button-icon" aria-hidden="true"></span>
                    <ng-content select=".wcs-ec-heading-content"></ng-content>
                  </button>
                </div>
                <section class="content-control"
                  [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody
                  [attr.aria-hidden]="!isExpanded">
                  <div class="ant-collapse-body"
                    [id]="'aria-helper-id-'+id" [ngClass]="{'accordion-body-display' : !isExpanded}">
                    <div class="body-content">
                      <ng-content select=".wcs-ec-body-content"></ng-content>
                    </div>
                  </div>
                </section>
              </div>
            </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: AccordionItemDirective, selector: "[uxdAccordionItem]", inputs: ["expanded"], outputs: ["opened", "closed", "expandedChanged"], exportAs: ["uxdAccordionItem"] }], animations: [accordionAnimations.accordionBodyExpansion] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExpandCollapaseWcs, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-expand-collapse-wcs]',
                    template: `<div uxdAccordionItem #accordionItem="uxdAccordionItem" #host class="collapse-block js-animateable" [ngClass]="{'ant-block-expanded' : accordionItem.expanded}">
              <div class="ant-collapse blue">
                <div class="ant-collapse-heading">
                  <button type="button"
                    [attr.aria-controls]="'aria-helper-id-'+id"
                    [attr.aria-expanded]="isExpanded"
                    (click)="toggleAccordionItem()" class="ant-collapse-button">
                    <span class="accordion-button-icon" aria-hidden="true"></span>
                    <ng-content select=".wcs-ec-heading-content"></ng-content>
                  </button>
                </div>
                <section class="content-control"
                  [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody
                  [attr.aria-hidden]="!isExpanded">
                  <div class="ant-collapse-body"
                    [id]="'aria-helper-id-'+id" [ngClass]="{'accordion-body-display' : !isExpanded}">
                    <div class="body-content">
                      <ng-content select=".wcs-ec-body-content"></ng-content>
                    </div>
                  </div>
                </section>
              </div>
            </div>`,
                    host: {
                        class: 'uxd-expand-collapse-wcs'
                    },
                    animations: [accordionAnimations.accordionBodyExpansion]
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { style: [{
                type: Input
            }], accordionItem: [{
                type: ViewChild,
                args: ['accordionItem', { static: true }]
            }], host: [{
                type: ViewChild,
                args: ['host', { static: true }]
            }] } });

//istanbul ignore next
class ChatAccordionCmp {
    constructor() {
        this._isExpanded = false;
        this.hideButtonIcon = false;
        this.startBlockExpanded = false;
        this.chatAccordionOutput = new EventEmitter();
    }
    /** Whether the current accordion item should be expanded. */
    get expanded() {
        return this._isExpanded;
    }
    set expanded(isExpanded) {
        this._isExpanded = isExpanded;
    }
    get isExpanded() {
        return this.accordionItem && this.accordionItem.expanded;
    }
    changeDisplay() {
        return !this.isExpanded;
    }
    get display() {
        return this.changeDisplay;
    }
    ngOnInit() {
    }
    outputData() {
        this.chatAccordionOutput.emit(true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ChatAccordionCmp, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ChatAccordionCmp, selector: "[data-uxd-chat-accordion-cmp]", inputs: { expanded: "expanded", bodyContent: "bodyContent", id: "id", hideButtonIcon: "hideButtonIcon", startBlockExpanded: "startBlockExpanded" }, outputs: { chatAccordionOutput: "chatAccordionOutput" }, host: { classAttribute: "uxd-chat-accordion-cmp" }, viewQueries: [{ propertyName: "accordionItem", first: true, predicate: ["accordionItem"], descendants: true }, { propertyName: "host", first: true, predicate: ["host"], descendants: true }], ngImport: i0, template: `
  <div uxdAccordionItem #accordionItem="uxdAccordionItem" #host [expanded]="expanded"
    class="collapse-block js-animateable" [ngClass]="{'ant-block-expanded' : accordionItem.expanded}">
    <div class="ant-collapse">
    <div class="ant-collapse-heading">
      <h3 class="chat-accordion-heading-text">
        <button type="button" class="ant-collapse-button" [id]="id" [attr.aria-expanded]="accordionItem.expanded" [attr.aria-controls]="id+'_section'"  (click)="accordionItem.toggle()">
          <span class="accordion-button-icon" aria-hidden="true"></span>
          <ng-content select="[chat-accodion-header]"></ng-content>
        </button>
      </h3>
      <button *ngIf="!hideButtonIcon" class="sub-header-button" (click)="outputData()"><span class="chat-accordion-heading-icon motif-icon motif-info"></span></button>
      <ng-content select="[chat-accodion-sub-header]"></ng-content>
      </div>
      <div class="ant-collapse-body content-control" [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody>
        <div class="body-content" [id]="id+'_section'">
          <ng-content> </ng-content>
          <div *ngIf="bodyContent" [innerHTML]="bodyContent">
          </div>
        </div>
      </div>
    </div>
  </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: AccordionItemDirective, selector: "[uxdAccordionItem]", inputs: ["expanded"], outputs: ["opened", "closed", "expandedChanged"], exportAs: ["uxdAccordionItem"] }], animations: [accordionAnimations.accordionBodyExpansion] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ChatAccordionCmp, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-chat-accordion-cmp]',
                    template: `
  <div uxdAccordionItem #accordionItem="uxdAccordionItem" #host [expanded]="expanded"
    class="collapse-block js-animateable" [ngClass]="{'ant-block-expanded' : accordionItem.expanded}">
    <div class="ant-collapse">
    <div class="ant-collapse-heading">
      <h3 class="chat-accordion-heading-text">
        <button type="button" class="ant-collapse-button" [id]="id" [attr.aria-expanded]="accordionItem.expanded" [attr.aria-controls]="id+'_section'"  (click)="accordionItem.toggle()">
          <span class="accordion-button-icon" aria-hidden="true"></span>
          <ng-content select="[chat-accodion-header]"></ng-content>
        </button>
      </h3>
      <button *ngIf="!hideButtonIcon" class="sub-header-button" (click)="outputData()"><span class="chat-accordion-heading-icon motif-icon motif-info"></span></button>
      <ng-content select="[chat-accodion-sub-header]"></ng-content>
      </div>
      <div class="ant-collapse-body content-control" [@expand]="isExpanded ? 'expanded' : 'collapsed'" #collapsebody>
        <div class="body-content" [id]="id+'_section'">
          <ng-content> </ng-content>
          <div *ngIf="bodyContent" [innerHTML]="bodyContent">
          </div>
        </div>
      </div>
    </div>
  </div>`,
                    host: {
                        class: 'uxd-chat-accordion-cmp'
                    },
                    animations: [accordionAnimations.accordionBodyExpansion]
                }]
        }], propDecorators: { expanded: [{
                type: Input
            }], bodyContent: [{
                type: Input
            }], id: [{
                type: Input
            }], hideButtonIcon: [{
                type: Input
            }], startBlockExpanded: [{
                type: Input,
                args: ['startBlockExpanded']
            }], chatAccordionOutput: [{
                type: Output
            }], accordionItem: [{
                type: ViewChild,
                args: ['accordionItem']
            }], host: [{
                type: ViewChild,
                args: ['host']
            }] } });

class AccordianModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordianModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AccordianModule, declarations: [AccordionItemDirective,
            AccordionDirective,
            ExpandCollapaseWcs,
            CollapseItemCmp,
            CollapseGroupCmp,
            AlternateToggleComponent,
            AlternateToggleBodyComponent,
            ExpansionPanelComponent,
            ExpansionHeaderComponent,
            ExpansionToggleComponent,
            ChatAccordionCmp], imports: [CommonModule], exports: [AccordionItemDirective,
            AccordionDirective,
            ExpandCollapaseWcs,
            CollapseItemCmp,
            CollapseGroupCmp,
            AlternateToggleComponent,
            AlternateToggleBodyComponent,
            ExpansionPanelComponent,
            ExpansionHeaderComponent,
            ExpansionToggleComponent,
            ChatAccordionCmp] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordianModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccordianModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        AccordionItemDirective,
                        AccordionDirective,
                        ExpandCollapaseWcs,
                        CollapseItemCmp,
                        CollapseGroupCmp,
                        AlternateToggleComponent,
                        AlternateToggleBodyComponent,
                        ExpansionPanelComponent,
                        ExpansionHeaderComponent,
                        ExpansionToggleComponent,
                        ChatAccordionCmp
                    ],
                    exports: [
                        AccordionItemDirective,
                        AccordionDirective,
                        ExpandCollapaseWcs,
                        CollapseItemCmp,
                        CollapseGroupCmp,
                        AlternateToggleComponent,
                        AlternateToggleBodyComponent,
                        ExpansionPanelComponent,
                        ExpansionHeaderComponent,
                        ExpansionToggleComponent,
                        ChatAccordionCmp
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AccordianModule, AccordionDirective, AccordionItemDirective, AccordionSelectionDispatcherService, AlternateToggleBodyComponent, AlternateToggleComponent, COLLAPSE_ANIMATION_TIMING, ChatAccordionCmp, CollapseGroupCmp, CollapseItemCmp, EXPAND_ANIMATION_TIMING, ExpandCollapaseWcs, ExpandCollapseTheme, ExpansionHeaderComponent, ExpansionPanelComponent, ExpansionToggleComponent, accordionAnimations };
